import React, { useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, X, Trash2, Pencil, Link as LinkIcon, Image as ImageIcon, Upload,
  MessageSquare, ExternalLink, Lightbulb, Send,
} from "lucide-react";
import { useAuth } from "../lib/auth";

const PALETTE = ["#0066ff", "#10B981", "#7C3AED", "#F59E0B", "#EC4899", "#EF4444", "#0EA5E9"];

/**
 * Inspiration Board.
 * - When `clientId` prop is omitted, the component scopes itself to the logged-in
 *   client (used at /inspiration in the client portal).
 * - When `clientId` is provided (admin viewing a client), all backend calls include
 *   `?client_id=...` so the admin sees that client's board.
 */
export default function InspirationBoard({ clientId: propClientId, embedded = false }) {
  const { user } = useAuth();
  const role = user?.role;
  const readOnly = role === "viewer";
  const clientId = propClientId || (role === "client" ? user?.user_id : null);
  const cidQS = clientId ? `?client_id=${clientId}` : "";

  const [sections, setSections] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null); // {mode:"create"} | section
  const [addingTo, setAddingTo] = useState(null); // section object
  const [viewingItem, setViewingItem] = useState(null);

  const load = async () => {
    try {
      const [s, i] = await Promise.all([
        api.get(`/inspiration/sections${cidQS}`),
        api.get(`/inspiration/items${cidQS}`),
      ]);
      setSections(s.data || []);
      setItems(i.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const grouped = useMemo(() => {
    const map = {};
    items.forEach((it) => { (map[it.section_id] = map[it.section_id] || []).push(it); });
    return map;
  }, [items]);

  const removeSection = async (sec) => {
    if (!window.confirm(`Delete the "${sec.name}" board? This removes all of its inspiration too.`)) return;
    try {
      await api.delete(`/inspiration/sections/${sec.id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const removeItem = async (it) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/inspiration/items/${it.id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="inspiration-board">
      {!embedded && (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">
              <Lightbulb size={12} className="text-amber-500" /> Inspiration Board
            </div>
            <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">Inspiration Board</h1>
            <p className="mt-2 max-w-xl text-sm text-zinc-600">
              Drop images, links, and references for every project. Add boards like "Logo," "Color palette,"
              or "Layouts" — and leave comments to tell us what you love.
            </p>
          </div>
          {!readOnly && (
            <button
              data-testid="inspiration-add-section"
              onClick={() => setEditingSection({ mode: "create" })}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#0066ff] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0052cc]"
            >
              <Plus size={14} /> New Board
            </button>
          )}
        </div>
      )}

      {embedded && !readOnly && (
        <div className="mb-5 flex justify-end">
          <button
            data-testid="inspiration-add-section"
            onClick={() => setEditingSection({ mode: "create" })}
            className="inline-flex items-center gap-1.5 rounded-md bg-[#0066ff] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0052cc]"
          >
            <Plus size={14} /> New Board
          </button>
        </div>
      )}

      <div className={embedded ? "" : "mt-8"}>
        {loading ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : sections.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-zinc-200 bg-white p-12 text-center">
            <Lightbulb size={32} className="mx-auto text-zinc-300" />
            <p className="mt-4 text-sm text-zinc-500">No boards yet. Create one to start collecting inspiration.</p>
            {!readOnly && (
              <button
                onClick={() => setEditingSection({ mode: "create" })}
                className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-[#0066ff] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0052cc]"
              >
                <Plus size={14} /> New Board
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            {sections.map((sec) => (
              <SectionPanel
                key={sec.id}
                section={sec}
                items={grouped[sec.id] || []}
                readOnly={readOnly}
                onEditSection={() => setEditingSection(sec)}
                onDeleteSection={() => removeSection(sec)}
                onAddItem={() => setAddingTo(sec)}
                onOpenItem={(it) => setViewingItem(it)}
                onDeleteItem={(it) => removeItem(it)}
              />
            ))}
          </div>
        )}
      </div>

      {editingSection && (
        <SectionModal
          initial={editingSection.mode === "create" ? null : editingSection}
          clientId={clientId}
          onClose={() => setEditingSection(null)}
          onSaved={() => { setEditingSection(null); load(); }}
        />
      )}
      {addingTo && (
        <NewItemModal
          section={addingTo}
          onClose={() => setAddingTo(null)}
          onSaved={() => { setAddingTo(null); load(); }}
        />
      )}
      {viewingItem && (
        <ItemModal
          item={viewingItem}
          readOnly={readOnly}
          onClose={() => setViewingItem(null)}
          onChanged={async () => {
            await load();
            // refresh selected item with latest comments
            const fresh = (await api.get(`/inspiration/items${cidQS}`)).data.find((x) => x.id === viewingItem.id);
            if (fresh) setViewingItem(fresh);
          }}
        />
      )}
    </div>
  );
}

function SectionPanel({ section, items, readOnly, onEditSection, onDeleteSection, onAddItem, onOpenItem, onDeleteItem }) {
  const color = section.color || "#0066ff";
  return (
    <div data-testid={`inspiration-section-${section.id}`} className="rounded-2xl border border-zinc-200 bg-white">
      <div
        className="flex items-center justify-between rounded-t-2xl border-b border-zinc-100 px-5 py-3"
        style={{ background: `linear-gradient(90deg, ${color}10 0%, white 60%)` }}
      >
        <div className="flex items-center gap-3">
          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
          <h2 className="text-sm font-semibold tracking-tight text-zinc-900">{section.name}</h2>
          <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold text-zinc-600">{items.length}</span>
        </div>
        <div className="flex items-center gap-1">
          {!readOnly && (
            <button
              data-testid={`inspiration-section-${section.id}-add`}
              onClick={onAddItem}
              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:border-[#0066ff] hover:text-[#0066ff]"
            >
              <Plus size={12} /> Add
            </button>
          )}
          {!readOnly && (
            <>
              <button onClick={onEditSection} className="rounded p-1.5 text-zinc-400 hover:bg-zinc-50 hover:text-zinc-700" title="Edit board">
                <Pencil size={13} />
              </button>
              <button onClick={onDeleteSection} className="rounded p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600" title="Delete board">
                <Trash2 size={13} />
              </button>
            </>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="px-5 py-8 text-center text-xs text-zinc-400">
          Nothing pinned yet. {!readOnly && "Click Add to start collecting inspiration."}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {items.map((it) => (
            <ItemTile
              key={it.id}
              item={it}
              readOnly={readOnly}
              onOpen={() => onOpenItem(it)}
              onDelete={() => onDeleteItem(it)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ItemTile({ item, readOnly, onOpen, onDelete }) {
  const commentCount = (item.comments || []).length;
  return (
    <div
      data-testid={`inspiration-item-${item.id}`}
      className="group relative cursor-pointer overflow-hidden rounded-lg border border-zinc-200 bg-zinc-50 transition-all hover:-translate-y-[2px] hover:border-[#0066ff] hover:shadow-md"
      onClick={onOpen}
    >
      <div className="aspect-square w-full overflow-hidden bg-zinc-100">
        {item.type === "image" && item.file_id ? (
          <img
            src={`${API_BASE}/files/${item.file_id}`}
            alt={item.title || ""}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-zinc-50 to-zinc-100 p-3 text-center">
            <LinkIcon size={20} className="text-zinc-400" />
            <div className="line-clamp-2 break-all text-[10px] font-medium text-zinc-600">
              {(() => {
                try { return new URL(item.url || "").hostname.replace(/^www\./, ""); }
                catch { return item.url || ""; }
              })()}
            </div>
          </div>
        )}
      </div>
      <div className="bg-white px-3 py-2">
        <div className="truncate text-xs font-medium text-zinc-900">{item.title || (item.type === "link" ? "Link" : "Image")}</div>
        <div className="mt-0.5 flex items-center justify-between text-[10px] text-zinc-500">
          <span className="flex items-center gap-1">
            {item.type === "image" ? <ImageIcon size={10} /> : <LinkIcon size={10} />}
            {item.type === "image" ? "Image" : "Link"}
          </span>
          {commentCount > 0 && (
            <span className="flex items-center gap-0.5 font-semibold text-[#0066ff]">
              <MessageSquare size={10} /> {commentCount}
            </span>
          )}
        </div>
      </div>
      {!readOnly && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 text-zinc-500 opacity-0 shadow transition-opacity hover:text-red-600 group-hover:opacity-100"
          title="Delete"
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}

function SectionModal({ initial, clientId, onClose, onSaved }) {
  const [form, setForm] = useState(initial || { name: "", color: "#0066ff" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name your board"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), color: form.color || "#0066ff" };
      if (initial) {
        await api.put(`/inspiration/sections/${initial.id}`, payload);
      } else {
        if (clientId) payload.client_id = clientId;
        await api.post("/inspiration/sections", payload);
      }
      toast.success(initial ? "Updated" : "Board created");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="inspiration-section-modal" className="w-full max-w-md rounded-2xl bg-white p-7 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h2 className="text-2xl font-semibold tracking-tight text-zinc-900">{initial ? "Edit Board" : "New Board"}</h2>
          <button onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={18} /></button>
        </div>

        <label className="mt-5 block text-sm">
          <span className="text-xs font-medium text-zinc-600">Board name</span>
          <input
            data-testid="inspiration-section-form-name"
            autoFocus
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Logo · Color palette · Layouts · Mood"
            className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
          />
        </label>
        <div className="mt-4">
          <div className="text-xs font-medium text-zinc-600">Accent color</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setForm({ ...form, color: c })}
                className={`h-8 w-8 rounded-full border-2 transition-all ${form.color === c ? "border-zinc-900 scale-110" : "border-white shadow"}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

        <div className="mt-7 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100">Cancel</button>
          <button data-testid="inspiration-section-save" onClick={save} disabled={saving} className="rounded-md bg-[#0066ff] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0052cc] disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewItemModal({ section, onClose, onSaved }) {
  const [mode, setMode] = useState("image"); // "image" | "link"
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (mode === "link") {
        if (!url.trim()) { toast.error("Paste a URL"); setSaving(false); return; }
        await api.post("/inspiration/items", {
          section_id: section.id,
          type: "link",
          url: url.trim(),
          title: title.trim(),
          note: note.trim(),
        });
      } else {
        if (!file) { toast.error("Pick an image to upload"); setSaving(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        if (title.trim()) fd.append("title", title.trim());
        if (note.trim()) fd.append("note", note.trim());
        await api.post(`/inspiration/sections/${section.id}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Added to board");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="inspiration-item-modal" className="w-full max-w-md rounded-2xl bg-white p-7 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-zinc-900">Add to "{section.name}"</h2>
            <div className="mt-1 text-xs text-zinc-500">Upload an image or paste a link.</div>
          </div>
          <button onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={18} /></button>
        </div>

        <div className="mt-5 flex items-center gap-1 rounded-lg border border-zinc-200 bg-white p-1 w-fit">
          {[
            { id: "image", label: "Image", icon: ImageIcon },
            { id: "link", label: "Link", icon: LinkIcon },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`inspiration-item-tab-${t.id}`}
              onClick={() => setMode(t.id)}
              className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${mode === t.id ? "bg-[#E5F0FF] text-[#0066ff]" : "text-zinc-600 hover:bg-zinc-50"}`}
            >
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>

        {mode === "image" ? (
          <div className="mt-5">
            <label className="block">
              <span className="text-xs font-medium text-zinc-600">Image file</span>
              <input
                data-testid="inspiration-item-file"
                type="file"
                accept="image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1 block w-full cursor-pointer text-sm text-zinc-700 file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-[#0066ff] file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-[#0052cc]"
              />
            </label>
          </div>
        ) : (
          <label className="mt-5 block text-sm">
            <span className="text-xs font-medium text-zinc-600">URL</span>
            <input
              data-testid="inspiration-item-url"
              autoFocus
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://dribbble.com/shots/…"
              className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
            />
          </label>
        )}

        <label className="mt-4 block text-sm">
          <span className="text-xs font-medium text-zinc-600">Title <span className="text-zinc-400">(optional)</span></span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What is this?"
            className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
          />
        </label>

        <label className="mt-4 block text-sm">
          <span className="text-xs font-medium text-zinc-600">Note <span className="text-zinc-400">(optional)</span></span>
          <textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What do you love about it?"
            className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
          />
        </label>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100">Cancel</button>
          <button data-testid="inspiration-item-save" onClick={save} disabled={saving} className="rounded-md bg-[#0066ff] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0052cc] disabled:opacity-50">
            {saving ? "Saving…" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ItemModal({ item, readOnly, onClose, onChanged }) {
  const { user } = useAuth();
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);

  const post = async () => {
    if (!comment.trim()) return;
    setPosting(true);
    try {
      await api.post(`/inspiration/items/${item.id}/comments`, { text: comment.trim() });
      setComment("");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setPosting(false);
    }
  };

  const removeComment = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/inspiration/items/${item.id}/comments/${c.id}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div data-testid="inspiration-item-view" className="grid w-full max-w-4xl grid-cols-1 overflow-hidden rounded-2xl bg-white shadow-xl md:grid-cols-[1.4fr_1fr]" onClick={(e) => e.stopPropagation()}>
        {/* media */}
        <div className="relative flex max-h-[80vh] items-center justify-center bg-zinc-900 p-2">
          {item.type === "image" && item.file_id ? (
            <img src={`${API_BASE}/files/${item.file_id}`} alt={item.title || ""} className="max-h-[78vh] w-full object-contain" />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-8 text-center text-white">
              <LinkIcon size={48} className="text-zinc-400" />
              <div className="break-all text-sm">{item.url}</div>
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#0066ff] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0052cc]"
              >
                Open <ExternalLink size={13} />
              </a>
            </div>
          )}
          <button onClick={onClose} className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80"><X size={16} /></button>
        </div>

        {/* details + comments */}
        <div className="flex max-h-[80vh] flex-col">
          <div className="border-b border-zinc-100 p-5">
            <div className="text-base font-semibold text-zinc-900">{item.title || (item.type === "link" ? "Link" : "Image")}</div>
            {item.note && <div className="mt-2 whitespace-pre-wrap text-sm text-zinc-600">{item.note}</div>}
            {item.type === "link" && item.url && (
              <a href={item.url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#0066ff] hover:underline">
                Open link <ExternalLink size={11} />
              </a>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-5">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Comments</div>
            <div className="mt-3 flex flex-col gap-3">
              {(item.comments || []).length === 0 ? (
                <div className="rounded-md border border-dashed border-zinc-200 px-4 py-6 text-center text-xs text-zinc-400">
                  No comments yet. Tell us what you love about it!
                </div>
              ) : (item.comments || []).map((c) => {
                const mine = c.author_id === user?.user_id;
                const isAdminAuthor = c.author_role === "admin";
                return (
                  <div key={c.id} className={`group relative rounded-md border px-3 py-2 ${isAdminAuthor ? "border-[#0066ff]/30 bg-[#E5F0FF]/40" : "border-zinc-200 bg-white"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-[11px] font-semibold text-zinc-700">
                        <span>{c.author_name || (isAdminAuthor ? "DeepThree" : "Client")}</span>
                        {isAdminAuthor && <span className="rounded-full bg-[#E5F0FF] px-1.5 py-0.5 text-[9px] font-semibold text-[#0066ff]">DeepThree</span>}
                      </div>
                      <div className="text-[10px] text-zinc-400">{c.created_at ? new Date(c.created_at).toLocaleString() : ""}</div>
                    </div>
                    <div className="mt-1 whitespace-pre-wrap text-sm text-zinc-800">{c.text}</div>
                    {(mine || user?.role === "admin") && (
                      <button
                        onClick={() => removeComment(c)}
                        className="absolute right-1 top-1 rounded p-1 text-zinc-300 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                        title="Delete comment"
                      >
                        <Trash2 size={11} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {!readOnly && (
            <div className="border-t border-zinc-100 p-4">
              <div className="flex items-end gap-2">
                <textarea
                  data-testid="inspiration-comment-input"
                  rows={2}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a comment…"
                  className="flex-1 resize-none rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) post();
                  }}
                />
                <button
                  data-testid="inspiration-comment-post"
                  onClick={post}
                  disabled={posting || !comment.trim()}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#0066ff] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0052cc] disabled:opacity-50"
                >
                  <Send size={13} /> Post
                </button>
              </div>
              <div className="mt-1 text-[10px] text-zinc-400">Tip: ⌘/Ctrl + Enter to post</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
